import React, { useState } from 'react';
import CalendarService, { SlotInquiryRequest } from '../../services/CalendarService';
import { CalendarSlot } from '../../types/calendar.types';
import { anfrageEinleitung, mindestPersonen } from '../../utils/kalenderAnfrage';
import './SlotInquiryModal.css';

/**
 * Terminanfrage-Dialog für geplante, noch nicht zur Online-Buchung freigegebene Termine
 * (Befund B7, Option "Sichtbar plus Anfrage").
 * Erzeugt keine Buchung, sondern eine Anfrage-E-Mail an das Frankenfloß-Team.
 */
interface SlotInquiryModalProps {
  slot: CalendarSlot;
  onClose: () => void;
}

const SlotInquiryModal: React.FC<SlotInquiryModalProps> = ({ slot, onClose }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  // T-181 (v1.8.57): Vorbelegung und Text aus der Mindestteilnehmerzahl des Termins, sonst 30
  const mindestzahl = mindestPersonen(slot);
  // Review v1.8.57: Vorbelegung höchstens 160 (Grenze der Prüfung unten)
  const [participantCount, setParticipantCount] = useState<number>(Math.min(mindestzahl, 160));
  const [message, setMessage] = useState('');
  const [website, setWebsite] = useState(''); // Honeypot, bleibt leer
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const formatDate = (dateStr: string): string => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatTime = (time: string): string => {
    const parts = time.split(':');
    return parts.length >= 2 ? `${parts[0]}:${parts[1]}` : time;
  };

  const validate = (): string | null => {
    if (!name.trim()) return 'Bitte geben Sie Ihren Namen an.';
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      return 'Bitte geben Sie eine gültige E-Mail-Adresse an.';
    }
    if (!participantCount || participantCount < 1 || participantCount > 160) {
      return 'Bitte geben Sie eine Teilnehmerzahl zwischen 1 und 160 an.';
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setError(null);
    setSubmitting(true);
    try {
      const request: SlotInquiryRequest = {
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim() || undefined,
        participantCount,
        message: message.trim() || undefined,
        website: website || undefined
      };
      const result = await CalendarService.submitSlotInquiry(slot.id, request);
      if (result.success) {
        setSuccess(result.message);
      } else {
        setError(result.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="inquiry-modal-overlay" onClick={onClose}>
      <div className="inquiry-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="inquiry-modal-header">
          <div>
            <h3>Termin anfragen</h3>
            <p className="inquiry-modal-date">
              {formatDate(slot.date)}, {formatTime(slot.time)} Uhr
            </p>
          </div>
          <button onClick={onClose} className="inquiry-modal-close" aria-label="Schließen">×</button>
        </div>

        {success ? (
          <div className="inquiry-modal-body">
            <div className="inquiry-success">
              <p>{success}</p>
              <p>Eine Eingangsbestätigung wurde an Ihre E-Mail-Adresse gesendet.</p>
            </div>
            <div className="inquiry-modal-actions">
              <button className="inquiry-btn-primary" onClick={onClose}>Schließen</button>
            </div>
          </div>
        ) : (
          <form className="inquiry-modal-body" onSubmit={handleSubmit}>
            <p className="inquiry-intro">
              {anfrageEinleitung(mindestzahl)} Gerne merken wir Ihre Anfrage vor und melden uns bei Ihnen.
            </p>

            <div className="inquiry-field">
              <label htmlFor="inquiry-name">Name *</label>
              <input
                id="inquiry-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={200}
                autoComplete="name"
              />
            </div>

            <div className="inquiry-field">
              <label htmlFor="inquiry-email">E-Mail-Adresse *</label>
              <input
                id="inquiry-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                maxLength={320}
                autoComplete="email"
              />
            </div>

            <div className="inquiry-field">
              <label htmlFor="inquiry-phone">Telefon (optional)</label>
              <input
                id="inquiry-phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                maxLength={50}
                autoComplete="tel"
              />
            </div>

            <div className="inquiry-field">
              <label htmlFor="inquiry-count">Voraussichtliche Teilnehmerzahl *</label>
              <input
                id="inquiry-count"
                type="number"
                min={1}
                max={160}
                value={participantCount}
                onChange={(e) => setParticipantCount(parseInt(e.target.value, 10) || 0)}
              />
            </div>

            <div className="inquiry-field">
              <label htmlFor="inquiry-message">Nachricht (optional)</label>
              <textarea
                id="inquiry-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                maxLength={2000}
                rows={4}
                placeholder="z.B. Anlass, Wunschleistungen, Rückfragen"
              />
            </div>

            {/* Honeypot gegen Bots, für Menschen unsichtbar */}
            <div className="inquiry-hp" aria-hidden="true">
              <label htmlFor="inquiry-website">Website</label>
              <input
                id="inquiry-website"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
              />
            </div>

            {error && <div className="inquiry-error">{error}</div>}

            <div className="inquiry-modal-actions">
              <button type="button" className="inquiry-btn-secondary" onClick={onClose} disabled={submitting}>
                Abbrechen
              </button>
              <button type="submit" className="inquiry-btn-primary" disabled={submitting}>
                {submitting ? 'Wird gesendet...' : 'Anfrage senden'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default SlotInquiryModal;
